import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

//
// Root
//
export function Dialog(
  props: React.ComponentProps<typeof DialogPrimitive.Root>
) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

//
// Trigger
//
export function DialogTrigger(
  props: React.ComponentProps<typeof DialogPrimitive.Trigger>
) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

//
// Portal
//
export function DialogPortal(
  props: React.ComponentProps<typeof DialogPrimitive.Portal>
) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

//
// Close
//
export function DialogClose(
  props: React.ComponentProps<typeof DialogPrimitive.Close>
) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

//
// Overlay
//
export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>((props, ref) => {
  const { style, ...rest } = props;

  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot="dialog-overlay"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "var(--app-overlay)",
        transition: "opacity 200ms",
        zIndex: 9998,
        ...style,
      }}
      {...rest}
    />
  );
});
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

//
// Content
//
export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>((props, ref) => {
  const { style, children, className, ...rest } = props;

  return (
    <DialogPortal >
      <DialogOverlay />

      <DialogPrimitive.Content
        ref={ref}
        className={className}
        data-slot="dialog-content"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",

          color: "var(--app-text)",
          backgroundColor: "var(--app-surface)",

          padding: "24px",
          borderRadius: "8px",
          border: "1px solid var(--app-border)",
          display: "grid",
          gap: "16px",
          zIndex: 9999,

          boxShadow: "var(--app-shadow-dialog)",

          transition: "all 200ms",

          ...style,
        }}
        {...rest}
      >
        {children}

        {/* CLOSE BUTTON */}
        <DialogPrimitive.Close
          aria-label="Close dialog"
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",

            color: "var(--app-text-muted)",
            background: "transparent",
            border: "none",
            cursor: "pointer",

            opacity: 0.7,
            transition: "opacity 150ms",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
        >
          <XIcon style={{ width: 16, height: 16 }} />
          <span style={{ position: "absolute", left: "-9999px" }}>Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

//
// Header
//
export function DialogHeader(
  props: React.ComponentProps<"div"> & { style?: React.CSSProperties }
) {
  const { style, ...rest } = props;

  return (
    <div
      data-slot="dialog-header"
      style={{
        display: "flex",
        flexDirection: "column",
        textAlign: "left",
        ...style,
      }}
      {...rest}
    />
  );
}

//
// Footer
//
export function DialogFooter(
  props: React.ComponentProps<"div"> & { style?: React.CSSProperties }
) {
  const { style, ...rest } = props;

  return (
    <div
      data-slot="dialog-footer"
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-end",
        gap: "8px",
        ...style,
      }}
      {...rest}
    />
  );
}

//
// Title
//
export function DialogTitle(
  props: React.ComponentProps<typeof DialogPrimitive.Title> & {
    style?: React.CSSProperties;
  }
) {
  const { style, ...rest } = props;

  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      style={{
        fontSize: "18px",
        fontWeight: 600,
        lineHeight: "1",
        ...style,
      }}
      {...rest}
    />
  );
}

//
// Description
//
export function DialogDescription(
  props: React.ComponentProps<typeof DialogPrimitive.Description> & {
    style?: React.CSSProperties;
  }
) {
  const { style, ...rest } = props;

  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      style={{
        fontSize: "14px",
        color: "#6b7280",
        ...style,
      }}
      {...rest}
    />
  );
}
